import { useState, useContext, useEffect, useCallback } from "react";
import '../../styles/page-titles.css'
import PermissionGate from "../../services/PermissionGate";
import styled from 'styled-components';
import { SidebarContext } from "../../AppRoutes";
import { getRelatorioDescontos } from "../../services/api";
import { nowTz } from '../../utils/timezone';
import Loading from "react-loading";
import { FaPercentage, FaUserTie, FaHashtag } from 'react-icons/fa';
import { MdDiscount, MdTrendingDown, MdCalendarToday } from 'react-icons/md';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { formatPriceWithSymbol } from '../../utils/currency';
import regionConfig from '../../config/regionConfig';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
    PieChart, Pie, Cell, Legend
} from 'recharts';

const Container = styled.div`
    display: flex;
    flex-direction: column;
    margin-left: ${({ sidebar }) => (sidebar ? '250px' : '100px')};
    min-height: 100vh;
    width: auto;
    transition: margin-left 200ms ease;
    background-color: #ffffff;
    padding: 24px;

    @media (max-width: 880px) {
        margin-left: 0;
        padding: 16px;
    }
`;

const Header = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 24px;
    flex-wrap: wrap;
    gap: 16px;

    .filters {
        display: flex;
        gap: 12px;
        flex-wrap: wrap;
    }

    input[type="date"] {
        padding: 8px 12px;
        border: 1px solid #ddd;
        border-radius: 8px;
        font-size: 14px;
    }

    button {
        padding: 8px 16px;
        background: linear-gradient(135deg, rgb(49, 140, 213), rgb(79, 172, 254));
        color: white;
        border: none;
        border-radius: 8px;
        cursor: pointer;
        font-weight: 500;
        transition: opacity 0.2s;

        &:hover {
            opacity: 0.9;
        }

        &:disabled {
            opacity: 0.6;
            cursor: not-allowed;
        }
    }

    @media (max-width: 600px) {
        h1 {
            font-size: 18px;
        }

        .filters {
            width: 100%;
        }

        input[type="date"], button {
            flex: 1;
        }
    }
`;

const CardsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 20px;
    margin-bottom: 30px;

    @media (max-width: 500px) {
        grid-template-columns: repeat(2, 1fr);
        gap: 12px;
    }
`;

const MetricCard = styled.div`
    background: white;
    padding: 20px;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    display: flex;
    flex-direction: column;
    gap: 8px;

    .icon {
        width: 40px;
        height: 40px;
        border-radius: 10px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 20px;
        margin-bottom: 8px;
    }

    .icon.blue { background: rgba(49, 140, 213, 0.1); color: rgb(49, 140, 213); }
    .icon.green { background: rgba(40, 167, 69, 0.1); color: #28a745; }
    .icon.orange { background: rgba(255, 152, 0, 0.1); color: #ff9800; }
    .icon.red { background: rgba(239, 68, 68, 0.1); color: #ef4444; }

    .label {
        font-size: 14px;
        color: #666;
    }

    .value {
        font-size: 28px;
        font-weight: 700;
        color: #333;
    }

    .sub {
        font-size: 12px;
        color: #999;
    }

    @media (max-width: 500px) {
        padding: 14px;
        .value { font-size: 22px; }
    }
`;

const ChartsRow = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px;
    margin-bottom: 30px;

    @media (max-width: 1000px) {
        grid-template-columns: 1fr;
    }
`;

const ChartCard = styled.div`
    background: white;
    padding: 24px;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);

    h3 {
        font-size: 16px;
        font-weight: 600;
        color: #333;
        margin-bottom: 20px;
        display: flex;
        align-items: center;
        gap: 8px;
    }

    .chart-container {
        height: 300px;
        width: 100%;
    }

    @media (max-width: 600px) {
        padding: 16px;

        .chart-container {
            height: 250px;
        }
    }
`;

const TableCard = styled.div`
    background: white;
    padding: 24px;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    margin-bottom: 30px;

    h3 {
        font-size: 16px;
        font-weight: 600;
        color: #333;
        margin-bottom: 20px;
        display: flex;
        align-items: center;
        gap: 8px;
    }

    .table-wrapper {
        overflow-x: auto;
        -webkit-overflow-scrolling: touch;
    }

    table {
        width: 100%;
        border-collapse: collapse;
        min-width: 700px;
    }

    thead th {
        background: #f8fafc;
        padding: 12px 16px;
        text-align: left;
        font-size: 12px;
        font-weight: 600;
        color: #666;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        border-bottom: 2px solid #e2e8f0;
    }

    tbody td {
        padding: 12px 16px;
        font-size: 14px;
        color: #333;
        border-bottom: 1px solid #f1f5f9;
    }

    tbody tr:hover {
        background: #f8fafc;
    }

    .discount-value {
        color: #ef4444;
        font-weight: 600;
    }

    .origin-badge {
        display: inline-block;
        padding: 4px 10px;
        border-radius: 12px;
        font-size: 11px;
        font-weight: 600;
        text-transform: uppercase;
    }

    .origin-pdv { background: rgba(49, 140, 213, 0.1); color: rgb(49, 140, 213); }
    .origin-cardapio { background: rgba(40, 167, 69, 0.1); color: #28a745; }
    .origin-whatsapp { background: rgba(37, 211, 102, 0.1); color: #25d366; }
    .origin-mesa { background: rgba(255, 152, 0, 0.1); color: #ff9800; }
    .origin-unknown { background: rgba(108, 117, 125, 0.1); color: #6c757d; }

    @media (max-width: 600px) {
        padding: 16px;
    }
`;

const EmptyState = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 60px 20px;
    text-align: center;

    .icon {
        font-size: 48px;
        color: #ddd;
        margin-bottom: 16px;
    }

    .title {
        font-size: 18px;
        font-weight: 600;
        color: #666;
        margin-bottom: 8px;
    }

    .subtitle {
        font-size: 14px;
        color: #999;
        max-width: 400px;
    }
`;

const LoadingContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 300px;
`;

const COLORS = ['#ef4444', '#f97316', '#eab308', '#22c55e', '#3b82f6', '#8b5cf6', '#ec4899'];

const RelatorioDescontos = () => {
    const { sidebar } = useContext(SidebarContext);
    const { t } = useTranslation('reports');

    const [isLoading, setIsLoading] = useState(true);
    const [data, setData] = useState({
        resumo: {
            total_descontos: 0,
            total_pedidos_com_desconto: 0,
            desconto_medio: 0,
            maior_desconto: 0,
            total_pedidos_periodo: 0,
            percentual_com_desconto: 0
        },
        por_dia: [],
        por_operador: [],
        pedidos: []
    });
    const [startDate, setStartDate] = useState(nowTz().subtract(30, 'days').format('YYYY-MM-DD'));
    const [endDate, setEndDate] = useState(nowTz().format('YYYY-MM-DD'));

    const fetchData = useCallback(async () => {
        setIsLoading(true);
        try {
            const response = await getRelatorioDescontos(startDate, endDate);
            if (response?.data?.success) {
                setData(response.data);
            }
        } catch (error) {
            console.error('[RELATORIO-DESCONTOS] Erro ao buscar dados:', error);
            toast.error(t('common.fetchError'));
        } finally {
            setIsLoading(false);
        }
    }, [startDate, endDate, t]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const getOriginLabel = (origin) => {
        const key = origin || 'unknown';
        return t(`discounts.origins.${key}`, { defaultValue: key });
    };

    const getOriginClass = (origin) => {
        const classMap = {
            pdv: 'origin-pdv',
            cardapio: 'origin-cardapio',
            whatsapp: 'origin-whatsapp',
            mesa: 'origin-mesa'
        };
        return classMap[origin] || 'origin-unknown';
    };

    const formatDate = (dateStr) => {
        const date = new Date(dateStr);
        return date.toLocaleDateString(regionConfig.currency.locale, {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatDayLabel = (dayStr) => {
        const [year, month, day] = dayStr.split('-');
        return `${day}/${month}`;
    };

    // Prepare chart data
    const barData = (data.por_dia || []).map(d => ({
        name: formatDayLabel(d.dia),
        desconto: d.total_desconto || 0,
        pedidos: d.quantidade || 0
    }));

    const pieData = (data.por_operador || []).map(op => ({
        name: op.nome,
        value: op.total_desconto || 0,
        pedidos: op.quantidade || 0
    }));

    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            const isCurrencyField = (name, dataKey) => {
                const currencyKeywords = ['desconto', 'discount', 'valor', 'value', 'total'];
                const lowerName = (name || '').toLowerCase();
                const lowerKey = (dataKey || '').toLowerCase();
                return currencyKeywords.some(kw => lowerName.includes(kw) || lowerKey.includes(kw));
            };

            return (
                <div style={{
                    background: 'white',
                    padding: '12px 16px',
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    border: '1px solid #e0e0e0'
                }}>
                    <p style={{ margin: 0, fontWeight: 600, color: '#333' }}>{label || payload[0]?.payload?.name}</p>
                    {payload.map((entry, index) => (
                        <p key={index} style={{ margin: '4px 0 0', color: entry.color || '#ef4444', fontSize: '13px' }}>
                            {entry.name}: {isCurrencyField(entry.name, entry.dataKey)
                                ? formatPriceWithSymbol(entry.value)
                                : entry.value?.toLocaleString?.(regionConfig.currency.locale) || entry.value}
                        </p>
                    ))}
                </div>
            );
        }
        return null;
    };

    const hasData = data.pedidos && data.pedidos.length > 0;

    return (
        <>
            <PermissionGate permissions={['default']}>
                <Container sidebar={sidebar}>
                    <Header>
                        <div className="page-title-with-icon">
                            <div className="page-icon"><MdDiscount /></div>
                            <h1 className="page-title">{t('discounts.title')}</h1>
                        </div>
                        <div className="filters" style={{ marginRight: '50px' }}>
                            <input
                                type="date"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                            />
                            <input
                                type="date"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                            />
                            <button onClick={fetchData} disabled={isLoading}>
                                {isLoading ? t('common.loading') : t('common.update')}
                            </button>
                        </div>
                    </Header>

                    {isLoading ? (
                        <LoadingContainer>
                            <Loading type="spin" color="rgb(49, 140, 213)" height={40} width={40} />
                        </LoadingContainer>
                    ) : !hasData ? (
                        <EmptyState>
                            <div className="icon"><MdDiscount /></div>
                            <div className="title">{t('discounts.noDiscounts')}</div>
                            <div className="subtitle">{t('discounts.noDiscountsSub')}</div>
                        </EmptyState>
                    ) : (
                        <>
                            <CardsGrid>
                                <MetricCard>
                                    <div className="icon red">
                                        <MdTrendingDown />
                                    </div>
                                    <span className="label">{t('discounts.totalDiscounts')}</span>
                                    <span className="value">
                                        {formatPriceWithSymbol(data.resumo?.total_descontos || 0)}
                                    </span>
                                    <span className="sub">{t('discounts.totalDiscountsSub')}</span>
                                </MetricCard>

                                <MetricCard>
                                    <div className="icon blue">
                                        <FaHashtag />
                                    </div>
                                    <span className="label">{t('discounts.ordersWithDiscount')}</span>
                                    <span className="value">{data.resumo?.total_pedidos_com_desconto || 0}</span>
                                    <span className="sub">{t('discounts.ordersWithDiscountSub', { value: data.resumo?.percentual_com_desconto || 0 })}</span>
                                </MetricCard>

                                <MetricCard>
                                    <div className="icon orange">
                                        <FaPercentage />
                                    </div>
                                    <span className="label">{t('discounts.avgDiscount')}</span>
                                    <span className="value">
                                        {formatPriceWithSymbol(data.resumo?.desconto_medio || 0)}
                                    </span>
                                    <span className="sub">{t('discounts.avgDiscountSub')}</span>
                                </MetricCard>

                                <MetricCard>
                                    <div className="icon green">
                                        <MdDiscount />
                                    </div>
                                    <span className="label">{t('discounts.largestDiscount')}</span>
                                    <span className="value">
                                        {formatPriceWithSymbol(data.resumo?.maior_desconto || 0)}
                                    </span>
                                    <span className="sub">{t('discounts.largestDiscountSub')}</span>
                                </MetricCard>
                            </CardsGrid>

                            <ChartsRow>
                                <ChartCard>
                                    <h3>
                                        <MdCalendarToday />
                                        {t('discounts.discountsPerDay')}
                                    </h3>
                                    <div className="chart-container">
                                        {barData.length > 0 ? (
                                            <ResponsiveContainer width="100%" height="100%">
                                                <BarChart data={barData}>
                                                    <CartesianGrid strokeDasharray="3 3" />
                                                    <XAxis dataKey="name" />
                                                    <YAxis />
                                                    <Tooltip content={<CustomTooltip />} />
                                                    <Bar dataKey="desconto" name={t('discounts.discountAmount')} fill="#ef4444" radius={[4, 4, 0, 0]} />
                                                </BarChart>
                                            </ResponsiveContainer>
                                        ) : (
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#999' }}>
                                                {t('common.noData')}
                                            </div>
                                        )}
                                    </div>
                                </ChartCard>

                                <ChartCard>
                                    <h3>
                                        <FaUserTie />
                                        {t('discounts.discountsByOperator')}
                                    </h3>
                                    <div className="chart-container">
                                        {pieData.length > 0 ? (
                                            <ResponsiveContainer width="100%" height="100%">
                                                <PieChart>
                                                    <Pie
                                                        data={pieData}
                                                        cx="50%"
                                                        cy="50%"
                                                        innerRadius={60}
                                                        outerRadius={100}
                                                        paddingAngle={3}
                                                        dataKey="value"
                                                        label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                                                        labelLine={false}
                                                    >
                                                        {pieData.map((entry, index) => (
                                                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                                        ))}
                                                    </Pie>
                                                    <Tooltip content={<CustomTooltip />} />
                                                    <Legend />
                                                </PieChart>
                                            </ResponsiveContainer>
                                        ) : (
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#999' }}>
                                                {t('common.noData')}
                                            </div>
                                        )}
                                    </div>
                                </ChartCard>
                            </ChartsRow>

                            <TableCard>
                                <h3>
                                    <MdDiscount />
                                    {t('discounts.detailTitle')}
                                </h3>
                                <div className="table-wrapper">
                                    <table>
                                        <thead>
                                            <tr>
                                                <th>{t('discounts.tableHeaders.order')}</th>
                                                <th>{t('discounts.tableHeaders.customer')}</th>
                                                <th>{t('discounts.tableHeaders.discount')}</th>
                                                <th>{t('discounts.tableHeaders.orderTotal')}</th>
                                                <th>{t('discounts.tableHeaders.operator')}</th>
                                                <th>{t('discounts.tableHeaders.origin')}</th>
                                                <th>{t('discounts.tableHeaders.date')}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(data.pedidos || []).map((pedido, index) => (
                                                <tr key={index}>
                                                    <td>#{pedido.id_pedido_counter || pedido.id_pedido}</td>
                                                    <td>{pedido.nome_cliente || '-'}</td>
                                                    <td className="discount-value">
                                                        -{formatPriceWithSymbol(pedido.desconto || 0)}
                                                    </td>
                                                    <td>{formatPriceWithSymbol(pedido.valor_total || 0)}</td>
                                                    <td>{pedido.operador || '-'}</td>
                                                    <td>
                                                        <span className={`origin-badge ${getOriginClass(pedido.origem_pedido)}`}>
                                                            {getOriginLabel(pedido.origem_pedido)}
                                                        </span>
                                                    </td>
                                                    <td>{formatDate(pedido.createdAt)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </TableCard>
                        </>
                    )}
                </Container>
            </PermissionGate>
        </>
    );
};

export default RelatorioDescontos;
